import ConfirmDialog from '@components/Shared/ConfirmDialog';
import Header from '@components/Shared/Layout/Header';
import { secondsSince, secondsUntilReclaimable } from '@components/Shared/seatTimes';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Box, Button, Card, CardContent, Chip, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import { useGetLicensesQuery } from '@store/api/licenseEndpoints';
import {
  useGetSeatSessionsQuery, useRevokeSeatSessionMutation, type SeatSession
} from '@store/api/seatSessionEndpoints';
import { useNow } from '@store/hooks/useNow';
import { useState } from 'react';

// Both read as every length of time on the panel does (`formatDuration`), never rounded up: a seat
// that can be reclaimed in 89 seconds used to read "1m", and one in 90 seconds "2m".
const formatAgo = (seconds: number) => seconds < 1 ? 'just now' : `${seconds.formatDuration()} ago`;

const formatCountdown = (seconds: number) => (seconds <= 0 ? 'now' : seconds.formatDuration());

const SeatSessionsPage = () => {
  // Seats change as clients come and go; poll rather than show a frozen picture.
  const { data: sessions = [], refetch, isFetching, fulfilledTimeStamp } = useGetSeatSessionsQuery(undefined, {
    pollingInterval: 30_000
  });
  // One clock for the table, a tick a second: "Reclaimable in" counts down from the answer's
  // figure as "Last heartbeat" counts up, so the two never disagree by the age of the last fetch
  // (it read 14 min 2 s beside "1 min 47 s ago" when that fetch was 49 s old).
  const now = useNow();
  const fetchedAt = fulfilledTimeStamp ?? now;
  const { data: licenses = [] } = useGetLicensesQuery();
  const [revokeSeatSession] = useRevokeSeatSessionMutation();

  // The seat the administrator is about to take off a device, held until they confirm it.
  const [releasing, setReleasing] = useState<SeatSession>();

  const licenseById = new Map(licenses.map(license => [license.licenseId, license]));

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Seat sessions'
        description={'A client keeps its seat by calling back every 15 minutes. Once a seat has ' +
          'gone quiet past that window, past the seat time its client was given and past any ' +
          'reservation, it is handed to the next client automatically.'}
      >
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
      </Header>

      {sessions.length === 0 ? (
        <Card><CardContent>
          <Typography variant='body2' color='text.secondary'>No seats are in use.</Typography>
        </CardContent></Card>
      ) : (
        <Card><CardContent>
          {/* Scrolls sideways on a narrow screen rather than cutting off the columns on the right,
              which here hold when a seat can go and the button that releases it. */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>License</TableCell>
                  <TableCell>App</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Last heartbeat</TableCell>
                  <TableCell>Reclaimable in</TableCell>
                  <TableCell align='right' />
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map(session => (
                  <TableRow key={session.sessionId}>
                    <TableCell>
                      <code>{session.id}</code>
                      <Typography variant='caption' display='block' color='text.secondary'>
                        {licenseById.get(session.licenseId)?.label ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size='small' variant='outlined' label={session.app ?? session.packageIdentifier} />
                    </TableCell>
                    <TableCell>
                      {session.deviceName || session.deviceId.slice(0, 8)}
                      <Typography variant='caption' display='block' color='text.secondary'>
                        {session.deviceModel}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {session.operatingSystem || session.runtimePlatform || '-'}
                      <Typography variant='caption' display='block' color='text.secondary'>
                        {session.version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Relative reads better at a glance; the exact stamp is one hover away. */}
                      <Tooltip title={new Date(session.lastUpdate).toFormattedDateTimeString()}>
                        <span>{formatAgo(secondsSince(session.lastUpdate, now))}</span>
                      </Tooltip>
                      {session.stale && <Chip size='small' color='warning' label='stale' sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      {formatCountdown(secondsUntilReclaimable(session.reclaimableInSeconds, fetchedAt, now))}
                      {session.allocationTimeRequest &&
                        new Date(session.allocationTimeRequest).getTime() > now &&
                        <Chip size='small' color='info' label='reserved' sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Release this seat'>
                        <IconButton
                          size='small'
                          aria-label='Release seat'
                          onClick={() => setReleasing(session)}
                        >
                          <LinkOffIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent></Card>
      )}

      {releasing && (
        <ConfirmDialog
          title='Release this seat?'
          confirmLabel='Release seat'
          confirmColor='warning'
          onClose={() => setReleasing(undefined)}
          onConfirm={() => revokeSeatSession({
            licenseId: releasing.licenseId, sessionId: releasing.sessionId
          })}
        >
          The seat returns to the pool at once and the next client can take it.{' '}
          <strong>{releasing.deviceName || releasing.deviceId.slice(0, 8)}</strong> keeps running
          on what it already has, but its token is dead. At its next call, within fifteen minutes,
          it is refused and has to be signed in again with the activation id and password of{' '}
          <code>{releasing.id}</code>, and if that license is full by then it waits until a seat
          frees up. A seat that is taken over because it went quiet, or that the device gives
          back itself, is different: that device keeps its token and simply takes a seat again
          when one is free.
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default SeatSessionsPage;
